import React, { useState, useEffect } from "react";
import { StyledForm } from "lib/styles/general";
import { CTAButton } from "lib/components/CTAButton";
import { CheckboxButton, Checkbox } from "lib/components/Checkbox";
import { KeyValue } from "lib/components/KeyValue";
import { Input } from "lib/components/Input";
import { Select } from "lib/components/Select";
import { fetchData, useFetch, Error } from "lib/helpers/fetchData";
import { Spinner } from "lib/components/Spinner";
import { getApiUrl } from "lib/helpers/getApiUrl";
import { ErrorModal } from "lib/components/Modal";

const MINUTE_IN_MS = 60 * 1000;
const THIRTY_DAYS_IN_MINUTES = 30 * 24 * 60;

const TIMEZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [];
  }
})();

const getJiraInstances = async () => {
  return await fetchData(`${getApiUrl("JIRA_MANAGEMENT_API")}/instances`);
};

const getCustomerConfig = async ({ customerId }) => {
  try {
    const existingCustomer = await fetchData(`${getApiUrl("TICKET_SERVICE")}/customers/${customerId}`);
    return existingCustomer;
  } catch (err) {
    if (err.statusCode === 404) {
      return undefined;
    }

    throw err;
  }
};

const loadDefaultConfigs = async () => fetchData(`${getApiUrl("TICKET_SERVICE")}/defaultSlaConfigurations`);

const BREACH_FIELDS = [
  { key: "p0Breach", label: "P0 Breach Time (min)" },
  { key: "p1Breach", label: "P1 Breach Time (min)" },
  { key: "p2Breach", label: "P2 Breach Time (min)" },
  { key: "p3Breach", label: "P3 Breach Time (min)" },
  { key: "phishBreach", label: "Phish Breach Time (min)" },
];

const INTEGER_PATTERN = /^\d+$/;

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const buildFormState = (data) => {
  const sla = data ?? {};
  const workingHoursObj = sla.calendar?.workingHours ?? {};

  return {
    newSlaEnabled: !!sla.newSlaEnabled,
    customSlaEnabled: !!sla.customSlaEnabled,
    timezone: sla.timezone ?? "",
    defaultSlaConfigurationId: sla.defaultSlaConfigurationId ?? "",
    ...Object.fromEntries(BREACH_FIELDS.map(({ key }) => [
      key,
      sla[key] == null ? "" : String(Math.max(1, Math.floor(sla[key] / MINUTE_IN_MS))),
    ])),
    calendar: {
      workingHours: DAYS_OF_WEEK.map((day) => {
        const existing = workingHoursObj[day.toLowerCase()];
        return existing
          ? { day, enabled: true, startTime: existing.startTime ?? "", endTime: existing.endTime ?? "" }
          : { day, enabled: false, startTime: "", endTime: "" };
      }),
      holidays: (sla.calendar?.holidays ?? []).map((h, hix) => ({
        id: hix,
        name: h.name ?? "",
        date: h.date ?? "",
        frequency: h.frequency ?? "annual",
      })),
    }
  };
};

const buildOriginalBreachValues = (data) =>
  Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, data?.[key]]));

const FieldError = ({ error, margin = "-0.75rem 0 10px" }) =>
  error ? <p style={{ color: "rgba(255, 0, 0, 0.8)", fontSize: "1.2rem", margin }}>{error}</p> : null;

export const Sla = ({ data, reload }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { data: customerData, isLoading: customerDataLoading, error: customerDataError } = useFetch(getCustomerConfig, { customerId: data.id });
  const [formState, setFormState] = useState(buildFormState(customerData));
  const [savedState, setSavedState] = useState(buildFormState(customerData));
  const [originalBreachValues, setOriginalBreachValues] = useState(buildOriginalBreachValues(customerData));
  const [changedBreachFields, setChangedBreachFields] = useState({});

  useEffect(() => {
    if (customerData !== undefined) {
      setFormState(buildFormState(customerData));
      setSavedState(buildFormState(customerData));
      setOriginalBreachValues(buildOriginalBreachValues(customerData));
      setChangedBreachFields({});
    }
  }, [customerData]);

  const { data: defaultConfigs } = useFetch(loadDefaultConfigs);
  const configs = defaultConfigs ?? [];

  const validate = () => {
    const newErrors = {};
    if (formState.newSlaEnabled) {
      if (!formState.timezone) {
        newErrors.timezone = "Timezone is required";
      }

      if (formState.customSlaEnabled) {
        BREACH_FIELDS.forEach(({ key }) => {
          if (!formState[key]) {
            newErrors[key] = "Required";
          } else if (!INTEGER_PATTERN.test(formState[key])) {
            newErrors[key] = "Must be a whole number";
          } else {
            const value = Number.parseInt(formState[key], 10);
            if (value < 1 || value > THIRTY_DAYS_IN_MINUTES) {
              newErrors[key] = `Must be in range 1 - ${THIRTY_DAYS_IN_MINUTES}`;
            }
          }
        });

        const enabledDays = formState.calendar.workingHours.filter((d) => d.enabled);

        if (enabledDays.length === 0) {
          newErrors.workingHours = "At least one working day must be configured";
        } else {
          enabledDays.forEach(({ day, startTime, endTime }) => {
            if (!startTime) newErrors[`${day}_start`] = "Required";
            if (!endTime) newErrors[`${day}_end`] = "Required";
            if (!!startTime && !!endTime) {
              const todayDateOnly = `${new Date().toISOString().split('T')[0]}`;
              const startTimeDate = new Date(`${todayDateOnly}T${startTime}:00Z`);
              const endTimeDate = new Date(`${todayDateOnly}T${endTime}:00Z`);

              if (startTime !== "00:00" && endTime !== "00:00" && (startTimeDate >= endTimeDate)) {
                newErrors[`${day}_start`] = "Start time must be before end time";
                newErrors[`${day}_end`] = "End time must be after start time";
              }
            }
          });
        }

        formState.calendar.holidays.forEach(({ id, name, date }) => {
          if (!name) newErrors[`holiday_${id}_name`] = "Required";
          if (!date) newErrors[`holiday_${id}_date`] = "Required";
        });
      } else if (!formState.defaultSlaConfigurationId) {
        newErrors.defaultSlaConfigurationId = "Default configuration is required";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const formStateToPostData = async () => {
    const jiraInstances = await getJiraInstances();
    const instance = jiraInstances.find((i) => i.id === data.connectors.jira.instanceId);

    const baseData = {
      customerId: data.id,
      customerName: data.name,
      jiraInstanceName: instance.name,
      jiraInstanceDomain: instance.domain,
      newSlaEnabled: formState.newSlaEnabled,
      customSlaEnabled: formState.customSlaEnabled,
      timezone: formState.timezone,
    };

    const holidays = formState.calendar.holidays.map(({ id: _id, ...rest }) => rest);
    const workingHours = Object.fromEntries(formState.calendar.workingHours.filter(({ enabled }) => enabled).map(({ day, startTime, endTime }) => [day.toLowerCase(), { startTime, endTime }]));

    const newSlaData = formState.customSlaEnabled ? {
      ...Object.fromEntries(BREACH_FIELDS.map(({ key }) => [
        key,
        changedBreachFields[key]
          ? Number.parseInt(formState[key], 10) * MINUTE_IN_MS
          : originalBreachValues[key],
      ])),
      calendar: {
        holidays,
        workingHours,
      }
    } : {
      defaultSlaConfigurationId: formState.defaultSlaConfigurationId,
    };

    const customData = formState.newSlaEnabled ? newSlaData : {};

    return { ...baseData, ...customData };
  }

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);

    try {
      const postData = await formStateToPostData();
      await fetchData(`${getApiUrl("TICKET_SERVICE")}/customers/update-sla-configuration?group=${data.groupName}`, JSON.stringify(postData), "POST");
      setSavedState(formState);
      setOriginalBreachValues(Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, postData[key]])));
      setChangedBreachFields({});
      setIsEditMode(false);
      reload?.();
    } catch (err) {
      setErrors({ form: "An error occurred while saving. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormState(savedState);
    setChangedBreachFields({});
    setErrors({});
    setIsEditMode(false);
  };

  const toggleNewSla = (checked) => {
    setFormState((prev) => ({
      ...prev,
      newSlaEnabled: checked,
      timezone: checked ? prev.timezone : "",
      customSlaEnabled: checked ? prev.customSlaEnabled : false,
    }));
    if (!checked) setErrors({});
  };

  const toggleCustomSla = (checked) => {
    setFormState((prev) => ({
      ...prev,
      customSlaEnabled: checked,
      defaultSlaConfigurationId: checked ? "" : prev.defaultSlaConfigurationId,
      ...Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, checked ? prev[key] : ""])),
      calendar: {
        workingHours: checked ? prev.calendar.workingHours : DAYS_OF_WEEK.map((day) => ({ day, enabled: false, startTime: "", endTime: "" })),
        holidays: checked ? prev.calendar.holidays : [],
      },
    }));
    setErrors({});
  };

  const updateCalendar = (updater) =>
    setFormState((prev) => ({ ...prev, calendar: updater(prev.calendar) }));

  const addHoliday = () =>
    updateCalendar((cal) => ({ ...cal, holidays: [...cal.holidays, { id: Date.now(), name: "", date: "", frequency: "annual" }] }));

  const updateHoliday = (id, field, value) =>
    updateCalendar((cal) => ({ ...cal, holidays: cal.holidays.map((h) => (h.id === id ? { ...h, [field]: value } : h)) }));

  const removeHoliday = (id) =>
    updateCalendar((cal) => ({ ...cal, holidays: cal.holidays.filter((h) => h.id !== id) }));

  const updateWorkingDay = (dayName, field, value) =>
    updateCalendar((cal) => ({
      ...cal,
      workingHours: cal.workingHours.map((d) =>
        d.day === dayName
          ? { ...d, [field]: value, ...(field === "enabled" && !value ? { startTime: "", endTime: "" } : {}) }
          : d
      ),
    }));

  if (customerDataLoading) return <Spinner />;
  if (customerDataError) return <Error message={customerDataError.message ?? "Unable to retrieve customer configuration"} />;

  return (
    <div style={{ position: "relative" }}>
      {!isEditMode && (
        <CTAButton
          style={{ position: "absolute", right: 0, top: 0, zIndex: 99 }}
          onClick={() => setIsEditMode(true)}
        >
          Edit
        </CTAButton>
      )}
      <StyledForm>
        <section style={{ paddingTop: 0 }}>
          {!isEditMode ? (
            <>
              <KeyValue
                text="New SLA Functionality"
                value={formState.newSlaEnabled ? "Enabled" : "Disabled"}
              />
              {formState.newSlaEnabled && (
                <>
                  <KeyValue text="Timezone" value={formState.timezone || "Not set"} />
                  <KeyValue
                    text="Custom SLA Configuration"
                    value={formState.customSlaEnabled ? "Enabled" : "Disabled"}
                  />
                  {formState.customSlaEnabled ? (
                    BREACH_FIELDS.map(({ key, label }) => (
                      <KeyValue key={key} text={label} value={formState[key] || "Not set"} />
                    ))
                  ) : (
                    <KeyValue
                      text="Default Configuration"
                      value={configs.find((c) => c.id === formState.defaultSlaConfigurationId)?.name || "Not set"}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <KeyValue
                text="New SLA Functionality"
                wrap={false}
                value={
                  <CheckboxButton
                    key={String(formState.newSlaEnabled)}
                    name="newSlaEnabled"
                    subtle
                    showWhenNull
                    label="Enable New SLA Functionality?"
                    defaultChecked={formState.newSlaEnabled}
                    onChange={(e) => toggleNewSla(e.target.checked)}
                  />
                }
              />
              {formState.newSlaEnabled && (
                <>
                  <Input
                    label="Timezone"
                    onChange={(e) => {
                      setErrors((prev) => ({ ...prev, timezone: null }));
                      setFormState((prev) => ({ ...prev, timezone: e.target.value }));
                    }}
                  >
                    <Select
                      name="timezone"
                      style={{ margin: "0.3rem 0 1rem", width: "100%" }}
                      value={formState.timezone}
                    >
                      <option value="">Select Timezone</option>
                      {TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </Select>
                  </Input>
                  <FieldError error={errors.timezone} />
                  <KeyValue
                    text="Custom SLA Configuration"
                    wrap={false}
                    value={
                      <CheckboxButton
                        key={String(formState.customSlaEnabled)}
                        name="customSlaEnabled"
                        subtle
                        showWhenNull
                        label="Enable Custom SLA Configuration?"
                        defaultChecked={formState.customSlaEnabled}
                        onChange={(e) => toggleCustomSla(e.target.checked)}
                      />
                    }
                  />
                  <Input
                    label="Default Configuration"
                    onChange={(e) => {
                      setErrors((prev) => ({ ...prev, defaultSlaConfigurationId: null }));
                      setFormState((prev) => ({ ...prev, defaultSlaConfigurationId: e.target.value }));
                    }}
                  >
                    <Select
                      name="defaultSlaConfigurationId"
                      style={{ margin: "0.3rem 0 1rem", width: "100%" }}
                      value={formState.defaultSlaConfigurationId}
                      disabled={formState.customSlaEnabled}
                    >
                      <option value="">Select Default Configuration</option>
                      {configs.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.name}
                        </option>
                      ))}
                    </Select>
                  </Input>
                  {!formState.customSlaEnabled && <FieldError error={errors.defaultSlaConfigurationId} />}
                  {formState.customSlaEnabled &&
                    BREACH_FIELDS.map(({ key, label }) => (
                      <Input
                        key={key}
                        label={label}
                        type="text"
                        inputMode="numeric"
                        value={formState[key]}
                        placeholder="Enter whole number"
                        onChange={(e) => {
                          setChangedBreachFields((prev) => ({ ...prev, [key]: true }));
                          setFormState((prev) => ({ ...prev, [key]: e.target.value }));
                        }}
                        error={errors[key]}
                      />
                    ))}
                </>
              )}
            </>
          )}
        </section>
        {formState.customSlaEnabled && (
          <section>
            {!isEditMode ? (
              <>
                <h3>Calendar</h3>
                <p style={{ fontWeight: "bold", fontSize: "1.33rem", margin: "0 0 0.75rem" }}>Working Hours</p>
                {formState.calendar.workingHours.some((d) => d.enabled) ? (
                  formState.calendar.workingHours
                    .filter((d) => d.enabled)
                    .map(({ day, startTime, endTime }) => (
                      <KeyValue key={day} text={day} value={`${startTime} – ${endTime}`} />
                    ))
                ) : (
                  <p style={{ fontSize: "1.33rem", fontStyle: "italic", opacity: 0.6, margin: 0 }}>No working hours configured</p>
                )}
                <p style={{ fontWeight: "bold", fontSize: "1.33rem", margin: "1.5rem 0 0.5rem" }}>Holidays</p>
                {formState.calendar.holidays.length === 0 ? (
                  <p style={{ fontSize: "1.33rem", fontStyle: "italic", opacity: 0.6, margin: 0 }}>No holidays configured</p>
                ) : (
                  formState.calendar.holidays.map((h) => (
                    <KeyValue key={h.id} text={h.name || "(unnamed)"} value={`${h.date} — ${h.frequency}`} />
                  ))
                )}
              </>
            ) : (
              <>
                <h3>Calendar</h3>
                <p style={{ fontWeight: "bold", fontSize: "1.33rem", margin: "0 0 0.75rem" }}>Working Hours</p>
                <FieldError error={errors.workingHours} margin="0 0 0.75rem" />
                {formState.calendar.workingHours.map((dayConfig) => (
                  <div
                    key={dayConfig.day}
                    style={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginBottom: "0.5rem" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0, width: "8rem" }}>
                      <Checkbox
                        key={String(dayConfig.enabled)}
                        small
                        defaultChecked={dayConfig.enabled}
                        onChange={(e) => updateWorkingDay(dayConfig.day, "enabled", e.target.checked)}
                      />
                      <span style={{ fontSize: "1.33rem" }}>{dayConfig.day}</span>
                    </div>
                    {dayConfig.enabled && (
                      <>
                        <div style={{ flex: 1 }}>
                          <Input
                            label="Start"
                            type="time"
                            value={dayConfig.startTime}
                            onChange={(e) => updateWorkingDay(dayConfig.day, "startTime", e.target.value)}
                          />
                          <FieldError error={errors[`${dayConfig.day}_start`]} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Input
                            label="End"
                            type="time"
                            value={dayConfig.endTime}
                            onChange={(e) => updateWorkingDay(dayConfig.day, "endTime", e.target.value)}
                          />
                          <FieldError error={errors[`${dayConfig.day}_end`]} />
                        </div>
                      </>
                    )}
                  </div>
                ))}
                <p style={{ fontWeight: "bold", fontSize: "1.33rem", margin: "1.5rem 0 0.5rem" }}>Holidays</p>
                {formState.calendar.holidays.length > 0 && (
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "0.25rem" }}>
                    <span style={{ flex: 2, fontSize: "1.33rem" }}>Name</span>
                    <span style={{ flex: 1, fontSize: "1.33rem" }}>Date</span>
                    <span style={{ flex: 1, fontSize: "1.33rem" }}>Frequency</span>
                    <CTAButton isSecondary style={{ visibility: "hidden" }}>Remove</CTAButton>
                  </div>
                )}
                {formState.calendar.holidays.map((h) => (
                  <div key={h.id} style={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginBottom: "0.5rem" }}>
                    <div style={{ flex: 2 }}>
                      <Input
                        type="text"
                        value={h.name}
                        onChange={(e) => updateHoliday(h.id, "name", e.target.value)}
                      />
                      <FieldError error={errors[`holiday_${h.id}_name`]} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input
                        type="date"
                        value={h.date}
                        onChange={(e) => updateHoliday(h.id, "date", e.target.value)}
                      />
                      <FieldError error={errors[`holiday_${h.id}_date`]} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input onChange={(e) => updateHoliday(h.id, "frequency", e.target.value)}>
                        <Select
                          name={`holiday-frequency-${h.id}`}
                          style={{ margin: "0.3rem 0 1rem", width: "100%" }}
                          value={h.frequency}
                        >
                          <option value="annual">Annual</option>
                          <option value="one-time">One Off</option>
                        </Select>
                      </Input>
                    </div>
                    <div style={{ marginTop: "0.33rem" }}>
                      <CTAButton isSecondary onClick={() => removeHoliday(h.id)}>Remove</CTAButton>
                    </div>
                  </div>
                ))}
                <CTAButton isSecondary onClick={addHoliday} style={{ marginTop: "0.5rem" }}>Add Holiday</CTAButton>
              </>
            )}
          </section>
        )}
      </StyledForm>
      {isEditMode && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem" }}>
          <CTAButton isSecondary onClick={handleCancel}>
            Cancel
          </CTAButton>
          <CTAButton isLoading={isLoading} onClick={handleSubmit}>
            Save
          </CTAButton>
        </div>
      )}
      {errors?.form && <ErrorModal data-testid="error" error={errors.form} hide={() => setErrors({})} />}
    </div>
  );
};
